import { zodTransformFirestoreTimestampToDate } from "../utils/zodUtils.mjs";
import { joiningNotificationsScheduledSchema } from "./classNotification.mjs";
import { zodFlavorId } from "./ids.mjs";
import { defineFirestoreCollection } from "./firebaseCollectionSchema.mjs";
import { PiiCategory, meta } from "./meta.mjs";
import z from "zod";

// src/models/class.ts
const CLASS_TYPE_DISPLAY_NAMES = {
	physical_fitness: "Physical Fitness",
	emotional_wellness: "Emotional Wellness",
	mind_body_practice: "Mind-Body Practice",
	other: "Other"
};
const getClassTypeDisplayName = (type) => {
	return CLASS_TYPE_DISPLAY_NAMES[type];
};
let ClassDeliveryType = /* @__PURE__ */ function(ClassDeliveryType) {
	ClassDeliveryType["WEBINAR"] = "webinar";
	ClassDeliveryType["INTERACTIVE"] = "interactive";
	return ClassDeliveryType;
}({});
const classSchema = z.object({
	name: z.string(),
	id: zodFlavorId(),
	description: z.string(),
	equipment: z.string(),
	type: z.enum(Object.keys(CLASS_TYPE_DISPLAY_NAMES)),
	defaultInstructorId: z.string().nullable(),
	defaultDeliveryType: meta(z.nativeEnum(ClassDeliveryType).nullable().optional(), { excludeFromBqSchema: true }),
	mustBeAble: z.string().optional(),
	testClass: z.boolean().default(false),
	photo: meta(z.object({
		url: z.string(),
		width: z.number(),
		height: z.number()
	}).optional(), { excludeFromBqSchema: true })
});
/**
* Classes collection definition
* Path: classes/{classId}
*/
const Classes = defineFirestoreCollection("classes", classSchema, void 0, {
	dwReplication: true,
	firestoreCollectionPath: "classes"
});
const classScheduleSchema = z.object({
	id: zodFlavorId(),
	classId: zodFlavorId(),
	instructorId: zodFlavorId(),
	scheduleStartDateUtc: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date()),
	scheduleEndDateUtc: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date()).nullable(),
	scheduleHour: z.number(),
	scheduleMinute: z.number(),
	scheduleDurationMinutes: z.number(),
	dayOfWeek: z.number(),
	scheduleTimeZone: z.string(),
	deliveryType: z.nativeEnum(ClassDeliveryType).nullable().optional()
});
/**
* Class schedules collection definition
* Path: class_schedules/{classScheduleId}
*/
const ClassSchedules = defineFirestoreCollection("class_schedules", classScheduleSchema, void 0, {
	dwReplication: true,
	firestoreCollectionPath: "class_schedules",
	exportDocumentBodyId: true
});
const CLASS_INSTANCE_STATUS = {
	SCHEDULED: "scheduled",
	CANCELLED: "cancelled",
	STARTED: "started",
	ENDED: "ended"
};
const classInstanceSchema = z.object({
	id: zodFlavorId(),
	classId: zodFlavorId(),
	instructorId: zodFlavorId(),
	classScheduleId: zodFlavorId().nullable(),
	occurrenceId: z.string().optional(),
	status: z.enum(Object.values(CLASS_INSTANCE_STATUS)),
	classStartTimeUtc: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date()),
	classDurationMinutes: z.number(),
	publishedRecordingId: zodFlavorId().nullable().optional(),
	deliveryType: z.nativeEnum(ClassDeliveryType).nullable().optional().default("interactive").transform((val) => val ?? "interactive"),
	/** @deprecated Prefer joiningNotificationsScheduled; kept for legacy docs until migration. */
	notificationsForJoiningSent: z.boolean().default(false).optional(),
	joiningNotificationsScheduled: joiningNotificationsScheduledSchema.optional(),
	notificationsForPublishedRecordingSentAt: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date()).optional(),
	showToCustomerWhenCanceled: z.boolean().optional()
});
/**
* Class instances collection definition
* Path: class_instances/{classInstanceId}
*/
const ClassInstances = defineFirestoreCollection("class_instances", classInstanceSchema, void 0, {
	dwReplication: true,
	firestoreCollectionPath: "class_instances"
});
const webinarDetailsSchema = z.object({
	topic: z.string(),
	startTime: z.string(),
	duration: z.number(),
	hostEmail: meta(z.string().optional(), { pii: { category: "email" } }),
	zoomUserId: z.string().optional(),
	timezone: z.string().nullable(),
	agenda: z.string().nullable()
});
const classInstanceWebinarSchema = z.object({
	id: zodFlavorId(),
	webinarId: zodFlavorId(),
	meetingUuid: z.string(),
	webinarUrl: meta(z.string(), { pii: { category: "url" } }),
	payloadHash: z.string(),
	payload: webinarDetailsSchema,
	meetingType: z.nativeEnum(ClassDeliveryType).nullable().optional().default("webinar").transform((val) => val ?? "webinar"),
	createdAt: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date()),
	updatedAt: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date())
});
/** Shape aligned with BigQuery export / `class_instance_webinar_reports.json` (codegen). */
const classInstanceWebinarReportBigQuerySchema = z.object({
	id: zodFlavorId(),
	webinarId: z.string(),
	topic: z.string(),
	startTime: z.string(),
	endTime: z.string(),
	duration: z.number(),
	hostEmail: meta(z.string(), { pii: { category: "email" } }),
	timezone: z.string(),
	agenda: z.string(),
	participants: meta(z.record(z.string(), z.unknown()), {
		bqType: "stringified_map",
		pii: { category: "freeText" }
	}),
	totalParticipants: z.number(),
	createdAt: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date()),
	updatedAt: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date())
});
const FirestoreClassInstanceWebinarReports = defineFirestoreCollection("class_instance_webinar_reports", classInstanceWebinarReportBigQuerySchema, void 0, {
	dwReplication: true,
	firestoreCollectionPath: "class_instance_webinar_reports"
});
let WebinarRegistrantStatus = /* @__PURE__ */ function(WebinarRegistrantStatus) {
	WebinarRegistrantStatus["PENDING"] = "pending";
	WebinarRegistrantStatus["QUEUED"] = "queued";
	WebinarRegistrantStatus["PROCESSING"] = "processing";
	WebinarRegistrantStatus["COMPLETED"] = "completed";
	WebinarRegistrantStatus["ERROR"] = "error";
	return WebinarRegistrantStatus;
}({});
const webinarRegistrantSchema = z.object({
	id: zodFlavorId(),
	customerId: zodFlavorId(),
	email: meta(z.string().email(), { pii: { category: "email" } }),
	firstName: meta(z.string(), { pii: { category: "name" } }),
	lastName: meta(z.string(), { pii: { category: "name" } }),
	joinUrl: meta(z.string(), { pii: { category: "url" } }),
	registrantId: z.string().optional(),
	createdAt: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date()),
	updatedAt: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date()),
	status: z.nativeEnum(WebinarRegistrantStatus).optional(),
	errorMessage: z.string().optional(),
	processedAt: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date()).optional()
}).refine((data) => data.id === data.customerId, {
	message: "Document ID and customerId must match",
	path: ["id", "customerId"]
});
const ClassInstanceWebinars = defineFirestoreCollection("class_instance_webinars", classInstanceWebinarSchema, { registrants: defineFirestoreCollection("registrants", webinarRegistrantSchema) });
const RECORDING_STATUS = {
	QUEUED: "queued",
	DOWNLOADING: "downloading",
	AVAILABLE: "available",
	FAILED: "failed"
};
let RecordingType = /* @__PURE__ */ function(RecordingType) {
	RecordingType["ZOOM_RECORDING"] = "zoom_recording";
	RecordingType["MANUAL_UPLOAD"] = "manual_upload";
	return RecordingType;
}({});
const zoomRecordingSchema = z.object({
	webinarId: zodFlavorId(),
	recordingId: zodFlavorId(),
	downloadUrl: meta(z.string().optional(), { pii: { category: "url" } }),
	fileName: z.string().optional(),
	fileSize: z.number(),
	recordingStart: z.string(),
	recordingEnd: z.string(),
	uploadedBy: z.string()
});
const manualUploadRecordingSchema = z.object({
	originalFileName: z.string(),
	uploadedBy: z.string(),
	uploadedAt: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date()),
	fileSize: z.number(),
	contentType: z.string()
});
const isZoomRecording = (metadata) => {
	return "webinarId" in metadata && "recordingId" in metadata;
};
const isManualUploadRecording = (metadata) => {
	return "originalFileName" in metadata && "uploadedBy" in metadata;
};
const classInstanceRecordingSchema = z.object({
	id: zodFlavorId(),
	classInstanceId: zodFlavorId(),
	recordingType: z.enum(Object.values(RecordingType)),
	status: z.enum(Object.values(RECORDING_STATUS)),
	fileSize: z.number().nullable(),
	firebaseStoragePath: z.string().nullable(),
	errorMessage: z.string().nullable().optional(),
	metadata: z.union([zoomRecordingSchema, manualUploadRecordingSchema]),
	createdAt: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date()),
	updatedAt: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date())
});

export { CLASS_INSTANCE_STATUS, CLASS_TYPE_DISPLAY_NAMES, ClassDeliveryType, ClassInstanceWebinars, ClassInstances, ClassSchedules, Classes, FirestoreClassInstanceWebinarReports, RECORDING_STATUS, RecordingType, WebinarRegistrantStatus, classInstanceRecordingSchema, classInstanceSchema, classInstanceWebinarReportBigQuerySchema, classInstanceWebinarSchema, classScheduleSchema, classSchema, getClassTypeDisplayName, isManualUploadRecording, isZoomRecording, manualUploadRecordingSchema, webinarDetailsSchema, webinarRegistrantSchema, zoomRecordingSchema };
//# sourceMappingURL=class.mjs.map